import { RECOMMEND_VISITOR_ID_BODY_KEY, RecommType } from '@/constants/recommend'
import { env } from '@/lib/runtime-env'
import { API_POST_RECOMMEND_WORLDS } from '@/services/api'
import { Room } from '@/services/cms'

type UnifiedBIRecommendationBaseParams = {
  recommType: RecommType
  countryCode?: string
  htcToken?: string
  cookieHeader?: string
  /** Visitor id (`csdataBid` cookie value), sent explicitly in the body — cookie forwarding is unreliable (missing cookie, or blocked in iframe contexts). */
  csdataBid?: string
  ua: string
  limit?: number
}

// world_id and world_id_source are mutually exclusive — sending both returns 400.
type UnifiedBIRecommendationParams =
  | (UnifiedBIRecommendationBaseParams & { worldId: string; worldIdSource?: never })
  | (UnifiedBIRecommendationBaseParams & { worldId?: never; worldIdSource?: string })

type RequestBody = {
  recomm_type: RecommType
  world_id?: string
  world_id_source?: string
  ua: string
  [RECOMMEND_VISITOR_ID_BODY_KEY]?: string
  limit: number
}

export const getUnifiedBIRecommendation = async ({
  recommType,
  worldId,
  worldIdSource,
  countryCode,
  htcToken,
  cookieHeader,
  csdataBid,
  ua,
  limit = 20,
}: UnifiedBIRecommendationParams): Promise<Room[]> => {
  try {
    const body: RequestBody = {
      recomm_type: recommType,
      ...(worldId && { world_id: worldId }),
      ...(worldIdSource && { world_id_source: worldIdSource }),
      ua,
      ...(csdataBid && { [RECOMMEND_VISITOR_ID_BODY_KEY]: csdataBid }),
      limit,
    }

    const extraHeaders: Record<string, string> = {
      ...(htcToken ? { authkey: htcToken } : cookieHeader && { cookie: cookieHeader }),
      ...(countryCode && { 'X-Country-Code': countryCode }),
    }

    const baseUrl = env('CMS_SERVER')

    const response = await fetch(`${baseUrl}${API_POST_RECOMMEND_WORLDS}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'User-Agent': env('USER_AGENT')!,
        ...extraHeaders,
      },
      body: JSON.stringify(body),
      cache: 'no-store',
    })

    if (response.status === 204) return []
    if (!response.ok)
      throw new Error(`Downstream Error: getUnifiedBIRecommendation ${response.status}`)

    const data = (await response.json()) as { results: Room[]; pages: unknown }
    return data?.results ?? []
  } catch (error) {
    console.error('getUnifiedBIRecommendation error:', error)
    return []
  }
}

export const getBecauseYouLiked = (
  htcToken: string,
  ua: string,
  limit?: number,
  csdataBid?: string,
): Promise<Room[]> =>
  getUnifiedBIRecommendation({
    recommType: 'content-based',
    worldIdSource: 'liked-room',
    htcToken,
    ua,
    csdataBid,
    limit,
  })

export const getBecauseYouLikedVariantB = (
  htcToken: string,
  ua: string,
  limit?: number,
  csdataBid?: string,
): Promise<Room[]> =>
  getUnifiedBIRecommendation({
    recommType: 'content-based-b',
    worldIdSource: 'liked-room',
    htcToken,
    ua,
    csdataBid,
    limit,
  })

export const getTrendingNearYouWorlds = (
  countryCode: string,
  ua: string,
  cookieHeader?: string,
  limit?: number,
  csdataBid?: string,
): Promise<Room[]> =>
  getUnifiedBIRecommendation({
    recommType: 'regional',
    countryCode,
    cookieHeader,
    ua,
    csdataBid,
    limit,
  })
